import * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useShops } from '../hooks/use-shops';
import { useDeleteShop } from '../hooks/use-delete-shop';
import { ShopCard } from '@/components/organisms/cards';
import { SearchBar } from '@/components/molecules/search-bar';
import { EmptyState } from '@/components/organisms/empty-state';
import { DeleteShopDialog } from '../components/DeleteShopDialog';
import { Skeleton } from '@/components/atoms/skeleton';
import { Button } from '@/components/atoms/button';
import { Card } from '@/components/atoms/card';
import { Badge } from '@/components/atoms/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/atoms/avatar';
import { ResponsiveDataList, ColumnDef } from '@/components/organisms/responsive-data-list';
import { useTranslation } from 'react-i18next';
import { Plus, MapPin, Globe, Wifi, WifiOff, Ban } from 'lucide-react';
import { useCanCreateShop, useSubscriptionLimits, useSubscriptionStore } from '@/lib/subscription';
import { LimitIndicator } from '@/lib/subscription';
import { toast } from 'sonner';
import { getShopLogoUrl } from '@/lib/utils/image';
import type { Shop } from '@/lib/types';

function getStatusConfig(status?: string, isActive?: boolean) {
  if (status === 'suspended') {
    return { variant: 'destructive' as const, icon: Ban, labelKey: 'shops.suspended' };
  }
  if (status === 'offline' || !isActive) {
    return { variant: 'secondary' as const, icon: WifiOff, labelKey: 'shops.offline' };
  }
  return { variant: 'default' as const, icon: Wifi, labelKey: 'shops.online' };
}

export function ShopListContainer() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [search, setSearch] = React.useState('');
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(10);

  const { data: allShops, isLoading, error } = useShops({ search });
  const deleteShop = useDeleteShop();

  // Subscription gating
  const shopAccess = useCanCreateShop();
  const limits = useSubscriptionLimits();
  const setCurrentCounts = useSubscriptionStore((state) => state.setCurrentCounts);
  const currentProductsCount = useSubscriptionStore((state) => state.currentProductsCount);

  // Client-side pagination
  const totalItems = allShops?.length ?? 0;

  // Update shops count in subscription store
  React.useEffect(() => {
    if (allShops) {
      setCurrentCounts(allShops.length, currentProductsCount);
    }
  }, [allShops, currentProductsCount, setCurrentCounts]);
  const totalPages = Math.ceil(totalItems / pageSize);
  const shops = React.useMemo(() => {
    if (!allShops) return [];
    const start = (page - 1) * pageSize;
    console.log(`Paginating shops: page ${page}, pageSize ${pageSize}, start index ${start}`);
    console.log('All shops:', allShops);
    return allShops.slice(start, start + pageSize);
  }, [allShops, page, pageSize]);

  // Reset to page 1 when search changes
  React.useEffect(() => {
    setPage(1);
  }, [search]);

  const handleRowClick = (shop: Shop) => {
    navigate(`/shops/${shop.refId || shop.id}`);
  };

  const handleDelete = (shop: Shop, params: { reason?: string; targetShopRefId?: string }) => {
    deleteShop.mutate(
      {
        id: shop.refId || shop.id,
        reason: params.reason,
        targetShopRefId: params.targetShopRefId,
      },
      {
        onSuccess: () => {
          toast.success(t('shops.deleteSuccess', 'Magasin supprimé avec succès'));
        },
        onError: () => {
          toast.error(t('shops.deleteError', 'Erreur lors de la suppression du magasin'));
        },
      }
    );
  };

  // Define table columns for desktop view
  const columns: ColumnDef<Shop>[] = React.useMemo(
    () => [
      {
        id: 'logo',
        header: '',
        cell: (shop) => (
          <Avatar className="h-10 w-10">
            <AvatarImage src={getShopLogoUrl(shop)} />
            <AvatarFallback className="font-bold bg-primary/10">
              {shop.name?.charAt(0)?.toUpperCase()}
            </AvatarFallback>
          </Avatar>
        ),
        className: 'w-16',
      },
      {
        id: 'name',
        header: t('shops.shopName'),
        cell: (shop) => (
          <span className="font-medium">{shop.name}</span>
        ),
        sortable: true,
      },
      {
        id: 'location',
        header: t('shops.location'),
        cell: (shop) => {
          const countryName = typeof shop.country === 'object' ? shop.country?.name : shop.country;
          return (
            <div className="flex items-center gap-2 text-muted-foreground">
              {shop.city && (
                <span className="flex items-center gap-1">
                  <MapPin className="size-3" />
                  {shop.city}
                </span>
              )}
              {countryName && (
                <span className="flex items-center gap-1">
                  <Globe className="size-3" />
                  {countryName}
                </span>
              )}
            </div>
          );
        },
      },
      {
        id: 'products',
        header: t('shops.products'),
        cell: (shop) => (
          <span className="text-muted-foreground">
            {shop.productCount ?? 0} {t('products.title').toLowerCase()}
          </span>
        ),
        sortable: true,
      },
      {
        id: 'status',
        header: t('common.status'),
        cell: (shop) => {
          const statusConfig = getStatusConfig(shop.status, shop.isActive);
          const StatusIcon = statusConfig.icon;
          return (
            <Badge variant={statusConfig.variant} className="gap-1">
              <StatusIcon className="size-3" />
              {t(statusConfig.labelKey)}
            </Badge>
          );
        },
      },
      {
        id: 'actions',
        header: '',
        cell: (shop) => (
          <DeleteShopDialog
            shop={shop}
            onConfirm={(params) => handleDelete(shop, params)}
            isLoading={deleteShop.isPending}
          />
        ),
        className: 'w-12',
      },
    ],
    [t, deleteShop.isPending]
  );

  // Card renderer for mobile view
  const renderCard = (shop: Shop, index: number) => (
    <ShopCard key={shop.id || `shop-${index}`} shop={shop} />
  );

  // Card skeleton for loading state
  const renderCardSkeleton = () => (
    <Card className="overflow-hidden">
      <Skeleton className="h-32 w-full" />
      <div className="p-4 space-y-2">
        <Skeleton className="h-5 w-3/4" />
        <Skeleton className="h-4 w-1/2" />
      </div>
    </Card>
  );

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-destructive">{t('shops.error')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Subscription Limit Indicator */}
      <LimitIndicator
        current={limits.shops.current}
        max={limits.shops.limit}
        label={t('subscription.features.shops')}
      />

      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <SearchBar
          onSearch={setSearch}
          placeholder={t('shops.searchPlaceholder')}
          className="sm:max-w-xs"
        />
        <Link to="/shops/new">
          <Button disabled={!shopAccess.canCreate}>
            <Plus className="size-4 mr-2" />
            {t('shops.addShop')}
          </Button>
        </Link>
      </div>

      <ResponsiveDataList
        data={shops}
        columns={columns}
        renderCard={renderCard}
        renderCardSkeleton={renderCardSkeleton}
        isLoading={isLoading}
        onRowClick={handleRowClick}
        keyExtractor={(shop, index) => shop.id || `shop-${index}`}
        cardGridClassName="grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
        page={page}
        pageSize={pageSize}
        totalPages={totalPages}
        totalItems={totalItems}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        emptyState={
          <EmptyState
            title={t('shops.emptyTitle')}
            description={t('shops.emptyDescription')}
            action={
              <Link to="/shops/new">
                <Button>
                  <Plus className="size-4 mr-2" />
                  {t('shops.addShop')}
                </Button>
              </Link>
            }
          />
        }
      />
    </div>
  );
}
